import React, { useEffect, useState, useCallback } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  getPushPermissionState,
  enablePushNotifications,
  refreshPushRegistrationIfGranted,
} from '../lib/push';

function showForegroundToast(payload) {
  const title = payload?.notification?.title || 'SocraQuest';
  const body = payload?.notification?.body || '';
  toast(title, { description: body });
}

/**
 * Bell button in the header:
 * - hidden when push is unsupported/unconfigured or permission denied
 * - "enable" prompt when permission not asked yet
 * - silently refreshes registration when already granted
 */
export function NotificationBell() {
  const [state, setState] = useState('unsupported');
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const s = await getPushPermissionState();
      if (!mounted) return;
      setState(s);
      if (s === 'granted') {
        // Keep token fresh + attach foreground handler; never prompts
        refreshPushRegistrationIfGranted(showForegroundToast);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const onEnable = useCallback(async () => {
    setBusy(true);
    const res = await enablePushNotifications(showForegroundToast);
    setBusy(false);
    if (res.ok) {
      setState('granted');
      toast.success(t('notifications.enabled', 'Notifications enabled!'));
    } else if (res.reason === 'permission-denied') {
      setState('denied');
      toast.error(t('notifications.denied', 'Notifications blocked. You can enable them in your browser/device settings.'));
    } else if (res.reason !== 'not-configured') {
      toast.error(t('notifications.failed', 'Could not enable notifications.'));
    }
  }, [t]);

  if (state === 'unsupported' || state === 'denied') return null;

  if (state === 'granted') {
    return (
      <div
        className="hidden sm:flex items-center text-teal-400/70"
        title={t('notifications.active', 'Notifications are on')}
        data-testid="notification-bell-active"
      >
        <BellRing className="w-4 h-4" />
      </div>
    );
  }

  return (
    <Button
      onClick={onEnable}
      disabled={busy}
      variant="ghost"
      size="sm"
      className="text-slate-400 hover:text-teal-400 hover:bg-slate-800"
      title={t('notifications.enable', 'Enable notifications')}
      data-testid="notification-bell-enable"
    >
      <Bell className="w-4 h-4" />
    </Button>
  );
}
